/* tslint:disable:max-line-length */
import {Inject, Injectable, OnDestroy} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {ChangeState} from '@docma-mm-core/model/change-state/change-state';
import {ChangeStatusEnum} from '@docma-mm-core/model/change-state/change-status.enum';
import {LoadState} from '@docma-mm-core/model/load-state/load-state';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {SyncState} from '@docma-mm-core/model/sync-state/sync-state';
import {SyncStatusEnum} from '@docma-mm-core/model/sync-state/sync-status.enum';
import {ViewPoint} from '@docma-mm-core/status/viewpoint.enum';
import {Guid} from 'guid-typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Attachment, AttachmentData, AttachmentSource} from './attachment';
import {AttachmentApiService} from './attachment-api.service';
import {AttachmentDbService} from './attachment-db.service';
import {AttachmentLoadingTypeEnum} from './attachment-loading-type.enum';
import {AttachmentType} from './attachment-type';
import {Observable, Subject, filter, first} from 'rxjs';
import {Activity} from '@docma-mm-core/activity/activity';
import {Fault, ViewFault} from '@docma-mm-core/fault/fault';
import {MmProjectLoaderService} from '../../mm-project/mm-project-loader.service';
import {User} from '@docma-mm-core/user/user';
import {PixFile} from '../../mm-attachments/mm-add-attachments-form/pix.service';
import {MmAnnotationService} from '../../mm-faults/mm-fault/mm-fault-attachments/mm-annotations/annotation.service';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {MmTranslateService} from '../../mm-translate.service';
import {NativeService} from '@mydocma/core/src/lib/native';
import {TaskFileDownloadService} from '@mydocma/task';
import { FaultPatchService } from '@docma-mm-core/fault/patch/fault-patch-service';
import {scaleBlob} from '../../mm-faults/mm-fault/mm-fault-attachments/mm-annotations/helpers/util';
import { IOfflineService } from '../../mm-offline/interfaces/offline-service.interface';
import { OFFLINE_SERVICE } from '../../mm-offline/interfaces/offline-service.token';
import * as pdfjsLib from 'pdfjs-dist';
import { PlatformModeStateService } from '../../mm-offline/services/platform-mode-state.service';


const objectUrlByAttachmentSource = new WeakMap<AttachmentSource, string>();

@UntilDestroy()
@Injectable()
export class AttachmentService implements OnDestroy {

  constructor(private readonly apiService: AttachmentApiService,
              private readonly projectLoaderService: MmProjectLoaderService,
              private dbService: AttachmentDbService,
              private readonly sanitizer: DomSanitizer,
              private readonly notificationService: NotificationService,
              private readonly translateService: MmTranslateService,
              private readonly nativeService: NativeService,
              private readonly taskDownloadService: TaskFileDownloadService,
              private readonly patchService: FaultPatchService,
              @Inject(OFFLINE_SERVICE) private offlineService: IOfflineService,
              private readonly platformModeStateService: PlatformModeStateService) {

    this.projectLoaderService.projectUserPermissions
      .pipe(untilDestroyed(this))
      .subscribe(u => this.user = u.data.User);

    pdfjsLib.GlobalWorkerOptions.workerSrc = '/assets/pdfjs/pdf.worker.min.js';
  }

  private annotationService: MmAnnotationService;

  private user: User;

  private attachmentsAddedSubject = new Subject<{ faultAttachments: Attachment[], activityAttachments: Attachment[], fault: Fault, activity: Activity }>();
  onAttachmentsAdded = this.attachmentsAddedSubject.asObservable();

  private pendingDeleteUids = new Set<string>();

  private attachmentChangedSubject = new Subject<{ attachment: Attachment, fault: Fault, activity: Activity }>();
  onAttachmentChanged = this.attachmentChangedSubject.asObservable();

  private attachmentDeletedSubject = new Subject<{ attachment: Attachment, fault: Fault, activity: Activity, modifiedOn: Date, modifier: User }>();
  onAttachmentDeleted = this.attachmentDeletedSubject.asObservable();

  openExtensions: string[] = ['pdf'];

  attachTo(annotationService: MmAnnotationService) {
    this.annotationService = annotationService;
  }

  ngOnDestroy(): void {
  }

  async createNewAttachment(data?: AttachmentData, file?: File, attachmentType?: AttachmentType) {
    const createDate = new Date(Date.now());
    const attachment = new Attachment();
    attachment.loadState = new LoadState({status: LoadStatusEnum.Loaded, data: {}, date: createDate});
    attachment.syncState = new SyncState({status: SyncStatusEnum.NotSync, date: createDate});
    const changeData = new AttachmentData(data);
    changeData.UniqueId = Guid.create().toString();
    changeData.SUid = Guid.create().toString();

    if (this.user) {
      changeData.Creator = {Account: this.user.Account, FirstName: this.user.FirstName, LastName: this.user.LastName};
    }

    if (!attachmentType) {
      attachmentType = (file.type?.startsWith('image') ? AttachmentType.Picture : undefined);
    }

    changeData.Type = attachmentType;
    changeData.EditableFor = ViewPoint.Contractor | ViewPoint.Customer | ViewPoint.Subcontractor;
    changeData.VisibleFor = ViewPoint.Contractor | ViewPoint.Customer | ViewPoint.Subcontractor;
    changeData.CreatedOn = changeData.CreatedOn ?? createDate;
    if (file) {
      Object.assign(changeData, Attachment.getDataFromFile(file));
      this.setAttachmentSource(attachment.source, file);

      const isImage = attachment.source.data.type?.startsWith('image');
      if (isImage) {
        const previewBlob = await scaleBlob(new Blob([file], {type: file.type}), 600, 600);
        const previewFile = new File([previewBlob], `${attachment.data.Description || 'annotated-thumb'}.png`, {
          type: 'image/png',
          lastModified: Date.now()
        });

        const thumbnailBlob = await scaleBlob(new Blob([file], {type: file.type}), 128, 128);
        const thumbnailFile = new File([thumbnailBlob], `${attachment.data.Description || 'annotated-thumb'}.png`, {
          type: 'image/png',
          lastModified: Date.now()
        });
        this.setAttachmentSource((attachment.thumbnail ??= new AttachmentSource()), thumbnailFile);
        this.setAttachmentSource((attachment.preview ??= new AttachmentSource()), previewFile);
      }

      const pixFile = file as PixFile;
      if (!changeData.Description) {
        if (pixFile.title) {
            changeData.Description = pixFile.title;
        }
        else {
            changeData.Description = file.name;
        }
      }
      if (!changeData.Comment) {
        if (pixFile.desc) {
          changeData.Comment = pixFile.desc;
        }
      }
    }
    attachment.changeState = new ChangeState({data: changeData, status: ChangeStatusEnum.New, date: createDate});
    return attachment;
  }

  // make private
  setAttachmentSource(attachmentSource: AttachmentSource, file: File) {
    if (file && file.size > 0) {
      const urlCreator = window.URL;

      const prevUrl = objectUrlByAttachmentSource.get(attachmentSource);
      if (prevUrl) {
        try {
          urlCreator.revokeObjectURL(prevUrl);
        } catch {

        }
        objectUrlByAttachmentSource.delete(attachmentSource);
      }

      const url = urlCreator.createObjectURL(file);
      objectUrlByAttachmentSource.set(attachmentSource, url);
      const safeUrl = this.sanitizer.bypassSecurityTrustUrl(url);
      attachmentSource.url = safeUrl;
      attachmentSource.name = file.name;
      attachmentSource.loadState = new LoadState({status: LoadStatusEnum.Loaded, data: file});
    }
  }

  async loadAttachmentSource(attachment: Attachment, attachmentSource: AttachmentSource,
                             extendedContentDisposition: boolean = false,
                             size?: { height: number, width: number },
                            cancelation?: Observable<any>  ): Promise<AttachmentSource> {

    attachmentSource.loadState = new LoadState({status: LoadStatusEnum.Loading});

    try {
      const file = await this.apiService.getAttachment(attachment.id, extendedContentDisposition, size && size.width, size && size.height, false, cancelation);
      this.setAttachmentSource(attachmentSource, file);
    } catch (error) {
      attachmentSource.loadState = new LoadState({status: LoadStatusEnum.Error, error: `Can't load from server`});
      throw error;
    }
    return attachmentSource;
  }

  async getRawAttachmentFile(attachment: Attachment, projectMydocmaUid: string, faultDownloaded: boolean): Promise<File> {
    if (faultDownloaded && this.platformModeStateService.isLocalMode) {
      return this.dbService.getFile(projectMydocmaUid, attachment.uid);
    }
    return this.apiService.getAttachment(attachment.id, false, 0, 0, false);
  }

  async downloadAttachmentSource(attachment: Attachment, attachmentSource: AttachmentSource,
    extendedContentDisposition: boolean = false,
    size?: { height: number, width: number },
    cancelation?: Observable<any>): Promise<AttachmentSource> {

    attachmentSource.loadState = new LoadState({status: LoadStatusEnum.Loading});

    let isCancelled = false;
    const cancelSub = cancelation?.subscribe(value => {
      isCancelled = value;
    });

    try {
    const fileName = attachment.loadState?.data?.FileName;

    if (isCancelled) {
      attachmentSource.loadState = new LoadState({status: LoadStatusEnum.NotLoaded, error: `Download cancelled`});
      return attachmentSource;
    }
    await this.offlineService.downloadAttachment(attachment.id, attachment.uid, fileName, extendedContentDisposition, size && size.width, size && size.height, false);

    if(isCancelled) {
      attachmentSource.loadState = new LoadState({status: LoadStatusEnum.NotLoaded, error: `Download cancelled`});
      return attachmentSource;
    }

    attachmentSource.loadState = new LoadState({status: LoadStatusEnum.Loaded, data: null});
    } catch (error) {
    attachmentSource.loadState = new LoadState({status: LoadStatusEnum.Error, error: `Can't load from server`});
    throw error;
    } finally{
      cancelSub?.unsubscribe();
    }
    return attachmentSource;
  }

  private async loadAttachmentSourceFromDb(
    projectMydocmaUid: string,
    attachment: Attachment,
    attachmentSource: AttachmentSource,
    size?: { height: number, width: number }
  ): Promise<AttachmentSource> {
    attachmentSource.loadState = new LoadState({status: LoadStatusEnum.Loading});

    try {
      const file = await this.dbService.getFile(projectMydocmaUid, attachment.uid);

      if (!file) {
        attachmentSource.loadState = new LoadState({status: LoadStatusEnum.Error, error: `Not found in db`});
        return attachmentSource;
      }

      const isPdf = file.type === 'application/pdf';
      const isThumbnail = size?.width === 128 && size?.height === 128;
      const isPreview = size?.width === 600 && size?.height === 600;

      if (isPdf && (isThumbnail || isPreview)) {
        const pdfFile = await generateSquarePdfFile(file, size);
        this.setAttachmentSource(attachmentSource, pdfFile);
        return attachmentSource;
      }

      if ((attachment.scribbles?.length ?? 0) > 0) {
        const imageSize = await getImageSize(file);
        const compositeImage = await this.annotationService.getCompositeImage(
          file,
          attachment.scribbles,
          imageSize,
          size,
        );
        this.setAttachmentSource(attachmentSource, compositeImage);
        return attachmentSource;
      }

      this.setAttachmentSource(attachmentSource, file);
    } catch(error) {
      if(error.message?.includes('FILE_NOT_FOUND')) {
        if(attachment.loadState.status === LoadStatusEnum.Loading) {
          attachmentSource.loadState = new LoadState({status: LoadStatusEnum.Loading});
        }else{
          attachmentSource.loadState = new LoadState({status: LoadStatusEnum.NotLoaded, error: `Attachment not found on device`});
        }
      }else{
        attachmentSource.loadState = new LoadState({status: LoadStatusEnum.Error, error: `Can't load from db`});
      }
    }

    return attachmentSource;
  }
  // Gets attachment thumbnail from cache or loads it from server
  async loadAttachmentThumbnail(projectMydocmaUid: string, attachment: Attachment,
                                size?: { height: number, width: number },
                                loadingType: AttachmentLoadingTypeEnum = AttachmentLoadingTypeEnum.FromServer,
                                force: boolean = false): Promise<AttachmentSource> {

    if (!size) {
      size = {height: 128, width: 128};
    }

    let thumbnail: AttachmentSource;

    if (!force) {
      if (attachment.thumbnail && attachment.thumbnail.loadState.status === LoadStatusEnum.Loaded) {
        thumbnail = attachment.thumbnail;
      } else if (attachment.source && attachment.source.loadState.status === LoadStatusEnum.Loaded && attachment.isNew) {
        thumbnail = await getThumbnail(attachment, this.sanitizer);
      }

      if (!thumbnail) {
        if (attachment.thumbnail && attachment.thumbnail.loadState.status !== LoadStatusEnum.NotLoaded) {
          thumbnail = attachment.thumbnail;
        } else if (attachment.source && attachment.source.loadState.status !== LoadStatusEnum.NotLoaded && attachment.isNew) {
          thumbnail = await getThumbnail(attachment, this.sanitizer);
        }
      }

      if (thumbnail) {
        attachment.thumbnail = thumbnail;
        return thumbnail;
      }
    }


    if (loadingType === AttachmentLoadingTypeEnum.FromServer) {
      // Don't wait for loading
      thumbnail = attachment.thumbnail || new AttachmentSource();
      this.loadAttachmentSource(attachment, thumbnail, false, size);
    } else if (loadingType === AttachmentLoadingTypeEnum.FromDb) {
      thumbnail = attachment.thumbnail || new AttachmentSource();
      this.loadAttachmentSourceFromDb(projectMydocmaUid, attachment, thumbnail, size);
    }
    attachment.thumbnail = thumbnail;
    return attachment.thumbnail;
  }

  async loadAttachmentPreview(projectMydocmaUid: string, attachment: Attachment,
                              size?: { height: number, width: number },
                              loadingType: AttachmentLoadingTypeEnum = AttachmentLoadingTypeEnum.FromServer,
                              force: boolean = false): Promise<AttachmentSource> {

    if (!size) {
      size = {height: 600, width: 600};
    }

    let preview: AttachmentSource;

    if (!force) {
      if (attachment.preview && attachment.preview.loadState.status === LoadStatusEnum.Loaded) {
        preview = attachment.preview;
      } else if (attachment.source && attachment.source.loadState.status === LoadStatusEnum.Loaded) {
        preview = await getPreview(attachment, this.sanitizer);
      }

      if (!preview) {
        if (attachment.preview && attachment.preview.loadState.status !== LoadStatusEnum.NotLoaded) {
          preview = attachment.preview;
        } else if (attachment.source && attachment.source.loadState.status !== LoadStatusEnum.NotLoaded) {
          preview = await getPreview(attachment, this.sanitizer);
        }
      }

      if (preview) {
        attachment.preview = preview;
        return preview;
      }
    }


    if (loadingType === AttachmentLoadingTypeEnum.FromServer) {
      // Don't wait for loading
      preview = attachment.preview || new AttachmentSource();
      this.loadAttachmentSource(attachment, preview, false, size);
    } else if (loadingType === AttachmentLoadingTypeEnum.FromDb) {
      preview = attachment.preview || new AttachmentSource();
      this.loadAttachmentSourceFromDb(projectMydocmaUid, attachment, preview, size);
    }
    attachment.preview = preview;
    return attachment.preview;
  }

  // Gets attachment source from cache or loads it from server
  async getAttachmentSource(attachment: Attachment, extendedContentDisposition: boolean = false, size?: { height: number, width: number }, projectMydocmaUid?: string, loadingType: AttachmentLoadingTypeEnum = AttachmentLoadingTypeEnum.FromServer): Promise<AttachmentSource> {

    // TODO IN NEXT SPRINT
    // TAKE SEARCH_ID FROM ACTIVITY
    // if (attachment.data.Type === AttachmentType.Writing && attachment.data?.OriginalFileName != null && attachment.data?.OriginalFileName !== '') {
    // }
    // else if (attachment.data?.Description != null && attachment.data.Description !== '') {
    // }
    if (!this.platformModeStateService.isLocalMode && attachment.source && attachment.source.loadState.status !== LoadStatusEnum.NotLoaded) {
      return attachment.source;
    } else if (this.platformModeStateService.isLocalMode && !!attachment.source.url && attachment.source.loadState.status === LoadStatusEnum.Loaded) {
      return attachment.source;
    } else if (this.platformModeStateService.isLocalMode && attachment.source.loadState.status === LoadStatusEnum.Loading && attachment.loadState.status === LoadStatusEnum.Loading) {
      return attachment.source;
    }

    attachment.source = attachment.source || new AttachmentSource();
    // Don't wait for loading
    if(loadingType === AttachmentLoadingTypeEnum.FromDb) {
      this.loadAttachmentSourceFromDb(projectMydocmaUid, attachment, attachment.source, size);
    } else{
      this.loadAttachmentSource(attachment, attachment.source, extendedContentDisposition, size);
    }
    
    return attachment.source;
  }

  async downloadAttachment(attachment: Attachment, downloadSource?: AttachmentSource) {
    if (!downloadSource) {
      downloadSource = await this.getAttachmentSource(attachment, true);
    }

    if (downloadSource?.loadState?.status !== LoadStatusEnum.Loaded) {
      await downloadSource.loadState$.pipe(
        filter(ls => ls.status === LoadStatusEnum.Loaded || ls.status === LoadStatusEnum.Error),
        first()
      ).toPromise();
    }

    if (!downloadSource?.url) {
      throw new Error('Download source is unavailable');
    }

    const downloadFileName: string = downloadSource?.name ?? attachment.data.FileName;
    const extension: string = downloadFileName.split('.').pop();

    if (this.nativeService.isWeb) {
      if ((window.navigator as any).msSaveOrOpenBlob) {
        (window.navigator as any).msSaveOrOpenBlob(downloadSource.data, downloadFileName);
      } else {
        const anchor = document.createElement('a');
        anchor.href = (downloadSource.url as any).changingThisBreaksApplicationSecurity;
        anchor.type = attachment.data.MimeType;

        const shouldOpen = this.openExtensions.findIndex(x => x.toLowerCase() === extension.toLowerCase()) !== -1;

        if (shouldOpen) {
          window.open(anchor.href, '_blank');
        } else {
          anchor.download = downloadFileName;
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
        }
      }
    } else {
      let open = false;
      if (extension?.toLowerCase() === 'pdf') {
        open = true;
      }
      await this.taskDownloadService.downloadFile(downloadSource.data, {
        fileName: downloadFileName,
        open,
        preventBrowserDownload: false
      });
      this.notificationService.success(this.translateService.instant('MM.attachment.downloadSuccess'));
    }

  }

  async addAttachments(files: File[], attachmentData: AttachmentData, fault: ViewFault, activity: Activity): Promise<boolean> {
    const newFaultAttachments: Attachment[] = [];
    const newActivityAttachments: Attachment[] = [];


    // files.count>1
    //



    for (const f of files) {
      const attachment = await this.createNewAttachment(attachmentData, f);
      if (fault && !activity) {
        fault.addAttachment(attachment);
        newFaultAttachments.push(attachment);
      } else if (activity) {
        activity.addAttachment(attachment);
        newActivityAttachments.push(attachment);
      }
    }

    const patches = await this.patchService.getByFaultUid(fault?.project?.MyDocmaUid, fault?.uid);
    const hasPatches = ((patches?.length ?? 0) > 0);
    if (!fault.isNew || hasPatches) {
      this.attachmentsAddedSubject.next({faultAttachments: newFaultAttachments, activityAttachments: newActivityAttachments, fault, activity});
      return true;
    }
    return false;
  }

  async changeAttachment(attachment: Attachment, fault: ViewFault) {
    const activity = (attachment.activity ? fault.activities.find(f => f.uid === attachment.activity.uid) : null);
    this.attachmentChangedSubject.next({attachment, fault, activity});
  }

  async deleteAttachment(attachment: Attachment, fault: ViewFault, activity: Activity) {
    const modifiedOn = await this.apiService.deleteAttachments([attachment]).toPromise();

    if (activity) {
      fault.removeAttachmentFromActivity(attachment, activity);
    } else {
      fault.removeAttachment(attachment);
    }

    this.attachmentDeletedSubject.next({attachment, fault, activity, modifiedOn, modifier: this.user});
    return modifiedOn;
  }

  async deleteUnsyncedAttachment(attachment: Attachment, fault: ViewFault, activity: Activity) {
    this.pendingDeleteUids.add(attachment.uid);

    if (activity) {
      fault.removeAttachmentFromActivity(attachment, activity);
    } else {
      fault.removeAttachment(attachment);
    }
  }

  isPendingDelete(attachmentUid: string): boolean {
    return this.pendingDeleteUids.has(attachmentUid);
  }

  async completePendingDelete(attachment: Attachment, fault: ViewFault, activity: Activity): Promise<boolean> {
    if (!this.pendingDeleteUids.has(attachment.uid)) return false;
    if (!attachment.id) return false;

    try {
      await this.apiService.deleteAttachments([attachment]).toPromise();
      this.pendingDeleteUids.delete(attachment.uid);
      return true;
    } catch (e) {
      console.error('[PENDING_DELETE] Failed to delete attachment', attachment.uid, e);
      return false;
    }
  }

  private convertBlobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(blob);
    });
  }
}

async function getThumbnail(attachment: Attachment, sanitizer: DomSanitizer) {
  const isImage = attachment.source.data.type?.startsWith('image');
  // && !attachment.source.data.type?.startsWith('application/octet-stream')) && !attachment.source.name?.endsWith('msg');
  if (!isImage) {
    // attachment.thumbnail.loadState = new LoadState({status: LoadStatusEnum.Loaded, data: attachment.thumbnail.data});
    return Promise.resolve(attachment.thumbnail);
  }

  const blob = await resizeImage(attachment.source.loadState.data, 128, 128);
  // const attachmentData = attachment.changeState.data;
  attachment.thumbnail.loadState = new LoadState({status: LoadStatusEnum.Loaded});
  const file = new File([blob], attachment.data.Description, {type: 'image/png', lastModified: attachment.changeState.date.getTime()});

  if (file && file.size > 0) {
    const urlCreator = window.URL;

    const prevUrl = objectUrlByAttachmentSource.get(attachment.thumbnail);
    if (prevUrl) {
      try {
        urlCreator.revokeObjectURL(prevUrl);
      } catch {

      }
      objectUrlByAttachmentSource.delete(attachment.thumbnail);
    }

    const url = urlCreator.createObjectURL(file);
    objectUrlByAttachmentSource.set(attachment.thumbnail, url);
    const safeUrl = sanitizer.bypassSecurityTrustUrl(url);
    attachment.thumbnail.url = safeUrl;
    attachment.thumbnail.name = file.name;
    attachment.thumbnail.loadState = new LoadState({status: LoadStatusEnum.Loaded, data: file});
  }

  return attachment.thumbnail;
}

async function getPreview(attachment: Attachment, sanitizer: DomSanitizer) {
  const isImage = attachment.source.data.type?.startsWith('image');
  // && !attachment.source.data.type?.startsWith('application/octet-stream')) && !attachment.source.name?.endsWith('msg');
  if (!isImage) {
    // attachment.thumbnail.loadState = new LoadState({status: LoadStatusEnum.Loaded, data: attachment.thumbnail.data});
    return Promise.resolve(attachment.preview);
  }

  const blob = await resizeImage(attachment.source.loadState.data, 600, 600);
  // const attachmentData = attachment.changeState.data;
  attachment.preview.loadState = new LoadState({status: LoadStatusEnum.Loaded});
  const file = new File([blob], attachment.data.Description, {type: 'image/png', lastModified: attachment.changeState.date.getTime()});

  if (file && file.size > 0) {
    const urlCreator = window.URL;

    const prevUrl = objectUrlByAttachmentSource.get(attachment.preview);
    if (prevUrl) {
      try {
        urlCreator.revokeObjectURL(prevUrl);
      } catch {

      }
      objectUrlByAttachmentSource.delete(attachment.preview);
    }

    const url = urlCreator.createObjectURL(file);
    objectUrlByAttachmentSource.set(attachment.preview, url);
    const safeUrl = sanitizer.bypassSecurityTrustUrl(url);
    attachment.preview.url = safeUrl;
    attachment.preview.name = file.name;
    attachment.preview.loadState = new LoadState({status: LoadStatusEnum.Loaded, data: file});
  }

  return attachment.preview;
}

function resizeImage(file: File, maxWidth: number, maxHeight: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);

    image.onload = () => {
      const width = image.width;
      const height = image.height;

      try {
        URL.revokeObjectURL(url);
      } catch {}

      if (width <= maxWidth && height <= maxHeight) {
        resolve(file);
        return;
      }

      let newWidth: number;
      let newHeight: number;

      if (width > height) {
        newHeight = height * (maxWidth / width);
        newWidth = maxWidth;
      } else {
        newWidth = width * (maxHeight / height);
        newHeight = maxHeight;
      }

      const canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;

      const context = canvas.getContext('2d');
      if (!context) {
        reject(new Error('2d context is not available'));
        return;
      }

      context.drawImage(image, 0, 0, newWidth, newHeight);

      canvas.toBlob(blob => {
        canvas.width = 0;
        canvas.height = 0;
        image.src = '';

        if (blob) resolve(blob);
        else reject(new Error('resizeImage returned null blob'));
      }, 'image/png');
    };

    image.onerror = e => {
      try {
        URL.revokeObjectURL(url);
      } catch {}
      reject(e);
    };

    image.src = url;
  });
}
// function downloadFile(file: File) {
//   const url = URL.createObjectURL(file);
//   const a = document.createElement("a");
//   a.href = url;
//   a.download = file.name;
//   a.click();
//   URL.revokeObjectURL(url);
// }

export function getImageSize(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      try {
        resolve({width: img.width, height: img.height});
      } finally {
        URL.revokeObjectURL(url);
        img.src = '';
      }
    };

    img.onerror = (e) => {
      try {
        URL.revokeObjectURL(url);
      } catch {}
      reject(e);
    };

    img.src = url;
  });
}

  export async function generateSquarePdfFile(
    pdfFile: File,
    size: { width: number; height: number }
  ): Promise<File> {

    if (size.width !== size.height) {
      throw new Error('Size must be square (width === height)');
    }

    const targetSize = size.width;

    const arrayBuffer = await pdfFile.arrayBuffer();

    const pdf = await pdfjsLib.getDocument({
      data: arrayBuffer
    }).promise;

    const page = await pdf.getPage(1);

    const viewport = page.getViewport({ scale: 1 });

    const scale = targetSize / Math.max(viewport.width, viewport.height);
    const scaledViewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = targetSize;
    canvas.height = targetSize;

    const context = canvas.getContext('2d')!;

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, targetSize, targetSize);

    const offsetX = (targetSize - scaledViewport.width) / 2;
    const offsetY = (targetSize - scaledViewport.height) / 2;

    await page.render({
      canvasContext: context,
      viewport: scaledViewport,
      transform: [1, 0, 0, 1, offsetX, offsetY]
    }).promise;

    pdf.destroy();

    const blob: Blob = await new Promise(resolve =>
      canvas.toBlob(b => resolve(b!), 'image/jpeg', 0.85)
    );

    canvas.width = 0;
    canvas.height = 0;

    return new File(
      [blob],
      pdfFile.name.replace(/\.pdf$/i, '') + `_${targetSize}.jpg`,
      { type: 'image/jpeg' }
    );
  }


